import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import PageHeader from './components/PageHeader';
import DataTable from './components/DataTable';
import DashboardLoader from './components/DashboardLoader';
import { usersAPI } from '../../services/api';
import './ContentPage.css';

const ROLES = ['admin', 'manager', 'editor', 'viewer', 'developer', 'designer', 'devops', 'hr'];

const ROLE_PERMISSIONS = {
  admin: {
    description: 'Full system access. Can manage users, settings, and all content.',
    permissions: ['View Content', 'Create Content', 'Edit Content', 'Delete Content', 'Manage Users', 'View Messages', 'Manage Newsletter', 'System Settings'],
    color: '#ef4444',
  },
  manager: {
    description: 'Can manage all content and view admin reports. Cannot manage users or system settings.',
    permissions: ['View Content', 'Create Content', 'Edit Content', 'Delete Content', 'View Messages', 'Manage Newsletter'],
    color: '#f59e0b',
  },
  editor: {
    description: 'Can create and edit content. Cannot delete or access admin features.',
    permissions: ['View Content', 'Create Content', 'Edit Content'],
    color: '#3b82f6',
  },
  viewer: {
    description: 'Read-only access. Can view all content but cannot make changes.',
    permissions: ['View Content'],
    color: '#6b7280',
  },
  developer: {
    description: 'Technical role. Can manage products, services, and technical content.',
    permissions: ['View Content', 'Create Content', 'Edit Content'],
    color: '#8b5cf6',
  },
  designer: {
    description: 'Design role. Can manage visual content like testimonials and team profiles.',
    permissions: ['View Content', 'Create Content', 'Edit Content'],
    color: '#ec4899',
  },
  devops: {
    description: 'Infrastructure role. Can view stats, manage partners and how-it-works.',
    permissions: ['View Content', 'Create Content', 'Edit Content'],
    color: '#14b8a6',
  },
  hr: {
    description: 'HR role. Can manage careers, team members, and view messages.',
    permissions: ['View Content', 'Create Content', 'Edit Content', 'View Messages'],
    color: '#f97316',
  },
};

const ALL_PERMISSIONS = ['View Content', 'Create Content', 'Edit Content', 'Delete Content', 'Manage Users', 'View Messages', 'Manage Newsletter', 'System Settings'];

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPermGuide, setShowPermGuide] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersAPI.list();
      setUsers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersAPI.update(userId, { role: newRole });
      fetchUsers();
    } catch (err) {
      alert('Update failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await usersAPI.update(u.id, { is_active: !u.is_active });
      fetchUsers();
    } catch (err) {
      alert('Update failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"?`)) return;
    try {
      await usersAPI.delete(u.id);
      fetchUsers();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const columns = [
    { key: 'username', label: 'Username', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'full_name', label: 'Full Name', sortable: true },
    {
      key: 'role', label: 'Role', sortable: true,
      render: (val, item) => (
        <select
          value={val}
          onChange={(e) => handleRoleChange(item.id, e.target.value)}
          disabled={item.id === currentUser?.id}
          className="users-role-select"
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      ),
    },
    {
      key: 'is_active', label: 'Status', sortable: true,
      render: (val) => (
        <span className={`users-status ${val ? 'users-status--active' : 'users-status--inactive'}`}>
          {val ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Manage user accounts and roles"
        backTo="/dashboard"
        icon={
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        }
        actions={
          <button
            className="btn-action btn-action--outline"
            onClick={() => setShowPermGuide(!showPermGuide)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            {showPermGuide ? 'Hide' : 'Role'} Permissions Guide
          </button>
        }
      />

      {/* Role Permissions Guide */}
      {showPermGuide && (
        <div className="perm-guide">
          <div className="perm-guide__header">
            <h3 className="perm-guide__title">Role Permissions Reference</h3>
            <p className="perm-guide__desc">Use this guide to understand what each role can do before assigning it to a user.</p>
          </div>

          {/* Permission Matrix Table */}
          <div className="perm-guide__matrix-wrap">
            <table className="perm-guide__matrix">
              <thead>
                <tr>
                  <th className="perm-guide__matrix-role-th">Role</th>
                  {ALL_PERMISSIONS.map((p) => (
                    <th key={p} className="perm-guide__matrix-th">{p.replace('Content', '').replace('Manage ', '').replace('System ', '').trim() || 'Content'}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROLES.map((role) => {
                  const info = ROLE_PERMISSIONS[role];
                  return (
                    <tr key={role}>
                      <td className="perm-guide__matrix-role-td">
                        <span className="perm-guide__role-dot" style={{ background: info.color }} />
                        <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{role}</span>
                      </td>
                      {ALL_PERMISSIONS.map((p) => (
                        <td key={p} className="perm-guide__matrix-td">
                          {info.permissions.includes(p) ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Role Cards */}
          <div className="perm-guide__cards">
            {ROLES.map((role) => {
              const info = ROLE_PERMISSIONS[role];
              return (
                <div key={role} className="perm-guide__card">
                  <div className="perm-guide__card-header">
                    <span className="perm-guide__card-dot" style={{ background: info.color }} />
                    <span className="perm-guide__card-name">{role}</span>
                  </div>
                  <p className="perm-guide__card-desc">{info.description}</p>
                  <div className="perm-guide__card-perms">
                    {info.permissions.map((p) => (
                      <span key={p} className="perm-guide__card-perm-tag">{p}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <DashboardLoader text="Loading users..." />
      ) : (
        <DataTable
          columns={columns}
          data={users}
          searchPlaceholder="Search users..."
          emptyMessage="No users found"
          actions={(item) =>
            item.id !== currentUser?.id ? (
              <>
                <button
                  className="btn-action"
                  style={{
                    padding: '0.3rem 0.75rem',
                    fontSize: '0.75rem',
                    background: 'rgba(245,158,11,0.1)',
                    color: '#f59e0b',
                  }}
                  onClick={() => handleToggleActive(item)}
                >
                  {item.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  className="btn-action btn-action--danger"
                  style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem' }}
                  onClick={() => handleDelete(item)}
                >
                  Delete
                </button>
              </>
            ) : null
          }
        />
      )}

      <style>{`
        .users-role-select {
          font-size: 0.8rem;
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--bg-surface);
          color: var(--text-primary);
          cursor: pointer;
          font-family: var(--font-sans);
        }
        .users-role-select:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .users-status {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.2rem 0.6rem;
          border-radius: 20px;
        }
        .users-status--active {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }
        .users-status--inactive {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        /* ── Permission Guide ── */
        .perm-guide {
          background: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          animation: slideDown 0.25s ease;
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .perm-guide__header {
          margin-bottom: 1.25rem;
        }
        .perm-guide__title {
          font-family: var(--font-serif);
          font-size: 1.1rem;
          color: var(--text-primary);
          margin-bottom: 0.25rem;
        }
        .perm-guide__desc {
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        /* Matrix table */
        .perm-guide__matrix-wrap {
          overflow-x: auto;
          margin-bottom: 1.25rem;
          border: 1px solid var(--border-color);
          border-radius: 8px;
        }
        .perm-guide__matrix {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
        }
        .perm-guide__matrix thead {
          background: var(--bg-surface-2);
        }
        .perm-guide__matrix th {
          padding: 0.6rem 0.5rem;
          text-align: center;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          font-size: 0.7rem;
          color: var(--text-secondary);
          border-bottom: 1px solid var(--border-color);
          white-space: nowrap;
        }
        .perm-guide__matrix-role-th {
          text-align: left !important;
          padding-left: 1rem !important;
          min-width: 110px;
        }
        .perm-guide__matrix-role-td {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1rem !important;
          white-space: nowrap;
        }
        .perm-guide__matrix td {
          padding: 0.5rem;
          text-align: center;
          border-bottom: 1px solid var(--border-color);
        }
        .perm-guide__matrix tbody tr:last-child td {
          border-bottom: none;
        }
        .perm-guide__matrix tbody tr:hover {
          background: var(--bg-surface-2);
        }
        .perm-guide__role-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        /* Role cards */
        .perm-guide__cards {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 0.75rem;
        }
        .perm-guide__card {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 1rem;
          transition: border-color 0.2s;
        }
        .perm-guide__card:hover {
          border-color: var(--text-muted);
        }
        .perm-guide__card-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .perm-guide__card-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .perm-guide__card-name {
          font-weight: 700;
          font-size: 0.85rem;
          text-transform: capitalize;
          color: var(--text-primary);
        }
        .perm-guide__card-desc {
          font-size: 0.75rem;
          color: var(--text-secondary);
          line-height: 1.5;
          margin-bottom: 0.75rem;
        }
        .perm-guide__card-perms {
          display: flex;
          flex-wrap: wrap;
          gap: 0.35rem;
        }
        .perm-guide__card-perm-tag {
          font-size: 0.65rem;
          font-weight: 600;
          padding: 0.15rem 0.5rem;
          border-radius: 20px;
          background: var(--bg-surface-2);
          color: var(--text-secondary);
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
}
